import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, MessageCircle, Send, Eye, X, Users, Search, Image, FileText, Upload, Trash2, Plus, Loader2, BookOpen } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import Header from '@/components/Header';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const WhatsAppMagazineBroadcast = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [recipients, setRecipients] = useState([]);
  const [selectedRecipients, setSelectedRecipients] = useState([]);
  const [showPreview, setShowPreview] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadingType, setUploadingType] = useState(''); // 'cover' or 'pdf'
  const pdfInputRef = useRef(null);
  const coverInputRef = useRef(null);
  
  // Search params
  const [searchParams, setSearchParams] = useState({
    magazine: 'Aabkari Times',
    recipient_type: 'both',
    status: 'ACTIVE'
  });
  
  // Magazine template data
  const [templateData, setTemplateData] = useState({
    edition_month: 'December 2025',
    main_headline: '',
    highlights: [
      { state: 'Uttar Pradesh', points: ['', '', ''] },
      { state: 'Kerala', points: [''] },
      { state: 'Maharashtra', points: [''] },
      { state: 'Goa', points: ['', ''] },
      { state: 'AlcoBev', points: [''] }
    ],
    website_link: 'https://aabkaritimes.com/29122025-2/',
    cover_image_url: 'https://aabkaritimes.com/wp-content/uploads/2025/12/Dec-Issue.jpg',
    pdf_url: 'https://customer-assets.emergentagent.com/job_dce94e7e-ae6a-4b06-bc83-0d5b54f19d95/artifacts/vziolja2_AT%20November%202025.pdf',
    pdf_filename: 'AT December 2025.pdf'
  });
  
  // Test phone input state
  const [testPhone, setTestPhone] = useState('');
  const [showTestModal, setShowTestModal] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);

  // Generate preview message matching approved template
  const generateTemplatePreview = () => {
    return `📖 *Aabkari Times - ${templateData.edition_month} Edition*

Dear Reader,

The latest edition is now available with updates from the Excise & AlcoBev industry!

Thank you for your continued support.

_Reply STOP to unsubscribe_

[Read Now] → ${templateData.website_link}`;
  };

  const fetchRecipients = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/whatsapp/recipients`, searchParams, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRecipients(response.data || []);
      setSelectedRecipients((response.data || []).map(r => r.id));
    } catch (error) {
      console.error('Error fetching recipients:', error);
      toast.error('Failed to fetch recipients');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedRecipients(recipients.map(r => r.id));
    } else {
      setSelectedRecipients([]);
    }
  };

  const handleSelectRecipient = (id, checked) => {
    if (checked) {
      setSelectedRecipients([...selectedRecipients, id]);
    } else {
      setSelectedRecipients(selectedRecipients.filter(r => r !== id));
    }
  };

  const handleHighlightChange = (sectionIndex, pointIndex, value) => {
    const newHighlights = [...templateData.highlights];
    newHighlights[sectionIndex].points[pointIndex] = value;
    setTemplateData({ ...templateData, highlights: newHighlights });
  };

  const handleAddPoint = (sectionIndex) => {
    const newHighlights = [...templateData.highlights];
    newHighlights[sectionIndex].points.push('');
    setTemplateData({ ...templateData, highlights: newHighlights });
  };

  const handleRemovePoint = (sectionIndex, pointIndex) => {
    const newHighlights = [...templateData.highlights];
    newHighlights[sectionIndex].points.splice(pointIndex, 1);
    setTemplateData({ ...templateData, highlights: newHighlights });
  };

  const handleAddSection = () => {
    const newHighlights = [...templateData.highlights, { state: '', points: [''] }];
    setTemplateData({ ...templateData, highlights: newHighlights });
  };

  const handleRemoveSection = (sectionIndex) => {
    const newHighlights = templateData.highlights.filter((_, i) => i !== sectionIndex);
    setTemplateData({ ...templateData, highlights: newHighlights });
  };

  const handleSectionNameChange = (sectionIndex, value) => {
    const newHighlights = [...templateData.highlights];
    newHighlights[sectionIndex].state = value;
    setTemplateData({ ...templateData, highlights: newHighlights });
  };

  const handleFileUpload = async (event, type) => {
    const file = event.target.files[0];
    if (!file) return;

    setUploading(true);
    setUploadingType(type);
    setUploadProgress(0);
    
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);

      const category = type === 'pdf' ? 'monthly_magazine/pdfs' : 'monthly_magazine/covers';
      const response = await axios.post(`${API}/upload/${category}`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: (progressEvent) => {
          const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(percent);
        }
      });

      const fileUrl = `${BACKEND_URL}${response.data.url}`;
      
      if (type === 'pdf') {
        setTemplateData(prev => ({ 
          ...prev, 
          pdf_url: fileUrl,
          pdf_filename: file.name 
        }));
        toast.success('PDF uploaded successfully!');
      } else {
        setTemplateData(prev => ({ ...prev, cover_image_url: fileUrl }));
        toast.success('Cover image uploaded successfully!');
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload file');
    } finally {
      setUploading(false);
      setUploadingType('');
      setUploadProgress(0);
    }
  };

  // Extract URL suffix from website link (e.g., "29122025-2/" from "https://aabkaritimes.com/29122025-2/")
  const getUrlSuffix = () => {
    try {
      const url = new URL(templateData.website_link);
      return url.pathname.replace(/^\//, ''); // Remove leading slash
    } catch {
      return templateData.website_link.replace('https://aabkaritimes.com/', '');
    }
  };

  const handleSendBulk = async () => {
    const selectedData = recipients.filter(r => selectedRecipients.includes(r.id));
    
    if (selectedData.length === 0) {
      toast.error('Please select at least one recipient');
      return;
    }

    setSending(true);
    setShowPreview(false);
    
    try {
      const token = localStorage.getItem('token');
      
      // Use the approved template bulk endpoint
      const response = await axios.post(`${API}/whatsapp/send-magazine-bulk`, {
        recipients: selectedData,
        edition_month: templateData.edition_month,
        url_suffix: getUrlSuffix(),
        cover_image_url: templateData.cover_image_url || null,
        pdf_url: templateData.pdf_url || null,
        template_sid: 'HX468aed3965d5d268cba6305c41b296f5',
        campaign_name: `${searchParams.magazine} - ${templateData.edition_month}`
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success(`Magazine broadcast sent to ${response.data.sent_count} recipients! (Cover + Template + PDF)`);
    } catch (error) {
      console.error('Error sending WhatsApp:', error);
      toast.error(error.response?.data?.detail || 'Failed to send WhatsApp messages');
    } finally {
      setSending(false);
    }
  };

  const handleTestMessage = async () => {
    if (!testPhone) {
      toast.error('Please enter a phone number');
      return;
    }
    
    setSendingTest(true);
    try {
      const token = localStorage.getItem('token');
      
      // Use the approved template endpoint
      const response = await axios.post(`${API}/whatsapp/send-magazine-template`, {
        phone: testPhone,
        edition_month: templateData.edition_month,
        url_suffix: getUrlSuffix(),
        cover_image_url: templateData.cover_image_url || null,
        pdf_url: templateData.pdf_url || null,
        template_sid: 'HX468aed3965d5d268cba6305c41b296f5'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        toast.success('Test message sent! Check your WhatsApp. (Cover + Template + PDF)');
        setShowTestModal(false);
      }
    } catch (error) {
      console.error('Error sending test:', error);
      toast.error(error.response?.data?.detail || 'Failed to send test message');
    } finally {
      setSendingTest(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <div className="max-w-7xl mx-auto p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-4">
            <Button variant="ghost" onClick={() => navigate('/')}>
              <ArrowLeft className="mr-2" size={20} />
              Back
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center">
                <BookOpen className="mr-3 text-green-600" size={28} />
                WhatsApp Magazine Broadcast
              </h1>
              <p className="text-gray-600">Send monthly magazine updates via WhatsApp</p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-6">
          {/* Left - Template Editor */}
          <div className="space-y-6">
            {/* Magazine Selection */}
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <h2 className="text-lg font-semibold mb-4 flex items-center">
                <FileText className="mr-2 text-green-600" size={20} />
                Magazine Edition Details
              </h2>
              
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div className="space-y-2">
                  <Label>Magazine</Label>
                  <select
                    value={searchParams.magazine}
                    onChange={(e) => setSearchParams({ ...searchParams, magazine: e.target.value })}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  >
                    <option value="Aabkari Times">Aabkari Times</option>
                    <option value="Sugar Times">Sugar Times</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <Label>Edition Month *</Label>
                  <Input
                    value={templateData.edition_month}
                    onChange={(e) => setTemplateData({ ...templateData, edition_month: e.target.value })}
                    placeholder="e.g., January 2026"
                  />
                </div>
              </div>

              <div className="space-y-2 mb-4">
                <Label>Main Headline</Label>
                <Input
                  value={templateData.main_headline}
                  onChange={(e) => setTemplateData({ ...templateData, main_headline: e.target.value })}
                  placeholder="e.g., UP'S IMFL INDUSTRY SCOPE & CHALLENGES AHEAD"
                />
              </div>

              <div className="space-y-2">
                <Label>Website Link *</Label>
                <Input
                  value={templateData.website_link}
                  onChange={(e) => setTemplateData({ ...templateData, website_link: e.target.value })}
                  placeholder="https://aabkaritimes.com/edition-link"
                />
              </div>
            </div>

            {/* Highlights Section */}
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold flex items-center">
                  ✨ Highlights of This Month
                </h2>
                <Button size="sm" variant="outline" onClick={handleAddSection}>
                  <Plus size={16} className="mr-1" /> Add Section
                </Button>
              </div>

              <div className="space-y-4 max-h-80 overflow-y-auto">
                {templateData.highlights.map((section, sIndex) => (
                  <div key={sIndex} className="border rounded-lg p-3 bg-gray-50">
                    <div className="flex items-center justify-between mb-2">
                      <Input
                        value={section.state}
                        onChange={(e) => handleSectionNameChange(sIndex, e.target.value)}
                        placeholder="Section name (e.g., Uttar Pradesh)"
                        className="font-semibold w-48 h-8 text-sm"
                      />
                      <div className="flex items-center space-x-2">
                        <Button size="sm" variant="ghost" onClick={() => handleAddPoint(sIndex)}>
                          <Plus size={14} />
                        </Button>
                        {templateData.highlights.length > 1 && (
                          <Button size="sm" variant="ghost" className="text-red-500" onClick={() => handleRemoveSection(sIndex)}>
                            <Trash2 size={14} />
                          </Button>
                        )}
                      </div>
                    </div>
                    {section.points.map((point, pIndex) => (
                      <div key={pIndex} className="flex items-center space-x-2 mb-1">
                        <span className="text-gray-400">•</span>
                        <Input
                          value={point}
                          onChange={(e) => handleHighlightChange(sIndex, pIndex, e.target.value)}
                          placeholder="Highlight point..."
                          className="flex-1 h-8 text-sm"
                        />
                        {section.points.length > 1 && (
                          <Button size="sm" variant="ghost" className="text-red-400 h-8 w-8 p-0" onClick={() => handleRemovePoint(sIndex, pIndex)}>
                            <X size={14} />
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </div>

            {/* File Uploads */}
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <h2 className="text-lg font-semibold mb-4 flex items-center">
                <Upload className="mr-2 text-green-600" size={20} />
                Attachments
              </h2>

              <div className="space-y-4">
                {/* Cover Image */}
                <div className="space-y-2">
                  <Label>Cover Image URL</Label>
                  <div className="flex space-x-2">
                    <Input
                      value={templateData.cover_image_url}
                      onChange={(e) => setTemplateData({...templateData, cover_image_url: e.target.value})}
                      placeholder="https://example.com/cover.jpg"
                      className="flex-1"
                      data-testid="cover-image-url"
                    />
                    <input
                      type="file"
                      ref={coverInputRef}
                      onChange={(e) => handleFileUpload(e, 'cover')}
                      accept="image/*"
                      className="hidden"
                    />
                    <Button size="sm" variant="outline" onClick={() => coverInputRef.current?.click()} disabled={uploading}>
                      <Upload size={14} />
                    </Button>
                  </div>
                  {templateData.cover_image_url && (
                    <div className="flex items-center space-x-2 mt-2">
                      <img 
                        src={templateData.cover_image_url} 
                        alt="Cover preview" 
                        className="h-16 rounded border"
                        onError={(e) => e.target.style.display = 'none'}
                      />
                      <Button size="sm" variant="ghost" className="text-red-500 text-xs" onClick={() => setTemplateData({...templateData, cover_image_url: ''})}>
                        <Trash2 size={12} className="mr-1" /> Remove
                      </Button>
                    </div>
                  )}
                </div>

                {/* PDF */}
                <div className="space-y-2">
                  <Label>Magazine PDF URL</Label>
                  <div className="flex space-x-2">
                    <Input
                      value={templateData.pdf_url}
                      onChange={(e) => setTemplateData({...templateData, pdf_url: e.target.value, pdf_filename: e.target.value.split('/').pop() || 'Magazine.pdf'})}
                      placeholder="https://example.com/magazine.pdf"
                      className="flex-1"
                      data-testid="pdf-url"
                    />
                    <input
                      type="file"
                      ref={pdfInputRef}
                      onChange={(e) => handleFileUpload(e, 'pdf')}
                      accept=".pdf"
                      className="hidden"
                    />
                    <Button size="sm" variant="outline" onClick={() => pdfInputRef.current?.click()} disabled={uploading}>
                      <Upload size={14} />
                    </Button>
                  </div>
                  {templateData.pdf_url && (
                    <div className="flex items-center space-x-2 mt-2 bg-gray-50 p-2 rounded">
                      <FileText size={20} className="text-red-500" />
                      <span className="text-xs text-gray-600 truncate flex-1">{templateData.pdf_filename || 'PDF attached'}</span>
                      <Button size="sm" variant="ghost" className="text-red-500 text-xs h-6" onClick={() => setTemplateData({...templateData, pdf_url: '', pdf_filename: ''})}>
                        <Trash2 size={12} />
                      </Button>
                    </div>
                  )}
                  
                  {/* Upload Progress Bar */}
                  {uploading && (
                    <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-medium text-blue-700">
                          Uploading {uploadingType === 'pdf' ? 'PDF' : 'Image'}...
                        </span>
                        <span className="text-sm font-medium text-blue-700">{uploadProgress}%</span>
                      </div>
                      <div className="w-full bg-blue-200 rounded-full h-2">
                        <div 
                          className="bg-blue-600 h-2 rounded-full transition-all duration-300" 
                          style={{ width: `${uploadProgress}%` }}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Right - Preview & Recipients */}
          <div className="space-y-6">
            {/* Message Preview */}
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <h2 className="text-lg font-semibold mb-4 flex items-center">
                <Eye className="mr-2 text-green-600" size={20} />
                Message Preview
              </h2>
              
              {/* Important Note */}
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4 text-sm">
                <p className="font-medium text-amber-800">📋 WhatsApp Business API Limitation:</p>
                <p className="text-amber-700 mt-1">
                  Only the approved template message will be sent. Cover image & PDF are accessible via the <strong>"Read Now"</strong> button link on your website.
                </p>
              </div>
              
              <div className="bg-[#e5ddd5] rounded-lg p-4 max-h-80 overflow-y-auto">
                {/* Template Message */}
                <div className="bg-white rounded-lg p-3 shadow">
                  <p className="font-semibold text-sm">Aabkari Times - {templateData.edition_month} Edition</p>
                  <p className="text-sm mt-2">Dear Reader,</p>
                  <p className="text-sm mt-2">The latest edition is now available with updates from the Excise & AlcoBev industry!</p>
                  <p className="text-sm mt-2">Thank you for your continued support.</p>
                  <p className="text-xs text-gray-500 mt-3 italic">Reply STOP to unsubscribe</p>
                  <div className="mt-3 border-t pt-2">
                    <a href={templateData.website_link} target="_blank" rel="noreferrer" className="bg-gray-100 text-gray-700 text-sm font-medium flex items-center justify-center py-2 rounded hover:bg-gray-200">
                      Read Now
                    </a>
                  </div>
                  <p className="text-right text-xs text-gray-400 mt-2">
                    {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </p>
                </div>
              </div>
            </div>

            {/* Recipients */}
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <h2 className="text-lg font-semibold mb-4 flex items-center">
                <Users className="mr-2 text-green-600" size={20} />
                Recipients
              </h2>
              
              <div className="grid grid-cols-3 gap-2 mb-4">
                <select
                  value={searchParams.recipient_type}
                  onChange={(e) => setSearchParams({ ...searchParams, recipient_type: e.target.value })}
                  className="h-9 rounded-md border px-2 text-sm"
                >
                  <option value="both">All</option>
                  <option value="members">Members</option>
                  <option value="advertisers">Advertisers</option>
                </select>
                <select
                  value={searchParams.status}
                  onChange={(e) => setSearchParams({ ...searchParams, status: e.target.value })}
                  className="h-9 rounded-md border px-2 text-sm"
                >
                  <option value="ACTIVE">Active</option>
                  <option value="ALL">All</option>
                </select>
                <Button onClick={fetchRecipients} disabled={loading} size="sm" style={{ backgroundColor: '#25D366' }}>
                  <Search size={16} className="mr-1" />
                  {loading ? '...' : 'Search'}
                </Button>
              </div>

              {recipients.length > 0 && (
                <>
                  <div className="flex items-center justify-between bg-green-50 p-2 rounded mb-2">
                    <span className="text-sm font-medium text-green-700">
                      {selectedRecipients.length} / {recipients.length} selected
                    </span>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        checked={selectedRecipients.length === recipients.length}
                        onCheckedChange={handleSelectAll}
                      />
                      <span className="text-xs">All</span>
                    </div>
                  </div>
                  
                  <div className="max-h-40 overflow-y-auto border rounded">
                    <table className="w-full text-xs">
                      <thead className="bg-gray-50 sticky top-0">
                        <tr>
                          <th className="p-1 w-8"></th>
                          <th className="p-1 text-left">Name</th>
                          <th className="p-1 text-left">Phone</th>
                        </tr>
                      </thead>
                      <tbody>
                        {recipients.slice(0, 50).map(r => (
                          <tr key={r.id} className="border-t">
                            <td className="p-1">
                              <Checkbox
                                checked={selectedRecipients.includes(r.id)}
                                onCheckedChange={(c) => handleSelectRecipient(r.id, c)}
                              />
                            </td>
                            <td className="p-1">{r.name}</td>
                            <td className="p-1 text-green-600">{r.phone}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {recipients.length > 50 && (
                      <p className="text-center text-xs text-gray-500 py-1">
                        +{recipients.length - 50} more recipients
                      </p>
                    )}
                  </div>
                </>
              )}
            </div>

            {/* Actions */}
            <div className="flex space-x-3">
              <Button
                onClick={() => setShowTestModal(true)}
                variant="outline"
                className="flex-1"
                disabled={!templateData.edition_month}
                data-testid="send-test-btn"
              >
                <MessageCircle size={18} className="mr-2" />
                Send Test
              </Button>
              <Button
                onClick={() => setShowPreview(true)}
                className="flex-1"
                style={{ backgroundColor: '#25D366' }}
                disabled={selectedRecipients.length === 0 || !templateData.edition_month}
                data-testid="send-bulk-btn"
              >
                <Send size={18} className="mr-2" />
                Send to {selectedRecipients.length} Recipients
              </Button>
            </div>
          </div>
        </div>

        {/* Confirmation Modal */}
        {showPreview && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
              <h3 className="text-lg font-semibold mb-4 flex items-center">
                <MessageCircle className="mr-2 text-green-600" size={20} />
                Confirm Send
              </h3>
              
              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                <p className="text-sm mb-2">
                  <strong>Magazine:</strong> {searchParams.magazine}
                </p>
                <p className="text-sm mb-2">
                  <strong>Edition:</strong> {templateData.edition_month}
                </p>
                <p className="text-sm mb-2">
                  <strong>Recipients:</strong> {selectedRecipients.length}
                </p>
                <p className="text-sm mb-2">
                  <strong>Cover Image:</strong> {templateData.cover_image_url ? '✅ Attached' : '❌ None'}
                </p>
                <p className="text-sm">
                  <strong>PDF:</strong> {templateData.pdf_url ? '✅ Attached' : '❌ None'}
                </p>
              </div>

              <p className="text-sm text-gray-600 mb-4">
                This will send WhatsApp messages to <strong>{selectedRecipients.length}</strong> recipients. 
                {templateData.pdf_url && ' PDF will be sent as a separate message.'}
              </p>

              <div className="flex space-x-3">
                <Button variant="outline" className="flex-1" onClick={() => setShowPreview(false)}>
                  Cancel
                </Button>
                <Button
                  className="flex-1"
                  style={{ backgroundColor: '#25D366' }}
                  onClick={handleSendBulk}
                  disabled={sending}
                >
                  {sending ? (
                    <>
                      <Loader2 className="mr-2 animate-spin" size={18} />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="mr-2" size={18} />
                      Confirm Send
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Test Message Modal */}
        {showTestModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
              <h3 className="text-lg font-semibold mb-4 flex items-center">
                <MessageCircle className="mr-2 text-green-600" size={20} />
                Send Test Message
              </h3>
              
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>WhatsApp Number *</Label>
                  <Input
                    value={testPhone}
                    onChange={(e) => setTestPhone(e.target.value)}
                    placeholder="+919876543210"
                    className="text-lg"
                    data-testid="test-phone-input"
                  />
                  <p className="text-xs text-gray-500">
                    Include country code (e.g., +91 for India)
                  </p>
                </div>

                <div className="bg-gray-50 rounded-lg p-3 text-sm">
                  <p className="font-medium mb-1">This test will send:</p>
                  <ul className="list-disc list-inside text-gray-600 space-y-1">
                    <li>Message with cover image {templateData.cover_image_url ? '✓' : '(no image set)'}</li>
                    {templateData.pdf_url && <li>PDF attachment as follow-up message</li>}
                  </ul>
                </div>
              </div>

              <div className="flex space-x-3 mt-6">
                <Button variant="outline" className="flex-1" onClick={() => setShowTestModal(false)}>
                  Cancel
                </Button>
                <Button
                  className="flex-1"
                  style={{ backgroundColor: '#25D366' }}
                  onClick={handleTestMessage}
                  disabled={!testPhone || sendingTest}
                  data-testid="confirm-test-btn"
                >
                  {sendingTest ? (
                    <>
                      <Loader2 className="mr-2 animate-spin" size={18} />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="mr-2" size={18} />
                      Send Test
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default WhatsAppMagazineBroadcast;
